import { jsonLdScriptProps } from 'react-schemaorg';
import type { Product, WithContext } from 'schema-dts';
import { Category, getMacroOfCategoryID } from '@sbt-web/category-utils';
import {
  AdItem,
  CategoryID,
  FeatureURI,
  ItemImage,
} from '@sbt-web/network/types';

export function getAdPriceRange(adItems: AdItem[]):
  | {
      min: number;
      max: number;
    }
  | { min: null; max: null } {
  const adsWithPrice = adItems.filter((adItem: AdItem) => {
    return (
      adItem.features[FeatureURI.Price] !== undefined &&
      adItem.features[FeatureURI.Price].values.length > 0
    );
  });

  const prices: number[] = adsWithPrice.map((adItem: AdItem) =>
    parseFloat(adItem.features[FeatureURI.Price]?.values[0].key || '')
  );

  if (prices.length === 0) {
    return { min: null, max: null };
  }

  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function imageUrlsFromAdItems(
  adItems: AdItem[],
  numberOfImages: number
): string | string[] {
  const imageUrls = adItems.reduce((acc: string[], adItem: AdItem) => {
    const urls: string[] = adItem.images
      .map((itemImage: ItemImage) => {
        if (itemImage != null) {
          return `${itemImage.cdnBaseUrl}?rule=gallery-desktop-2x-auto`;
        } else {
          return '';
        }
      })
      .filter((url: string) => url.length > 0);

    return [...acc, ...urls];
  }, []);
  return imageUrls.slice(0, numberOfImages);
}

const shouldShowSchema = ({
  category,
  page,
}: {
  category: Category;
  page: number;
}): boolean => {
  if (page > 1) {
    return false;
  }

  const macro = getMacroOfCategoryID(category.id);

  if (macro.id === CategoryID.Tutte || macro.id === CategoryID.Immobili) {
    return false;
  } else
    return !(
      macro.id === CategoryID.LavoroServizi &&
      category.id !== CategoryID.Attrezzature
    );
};

interface SchemaComponentProps {
  items: AdItem[];
  category: Category;
  description: string;
  searchQuery?: string;
  brandName?: string;
  modelName?: string;
  page: number;
  itemsCount: number;
}

const listingSchema = ({
  items,
  itemsCount,
  category,
  brandName,
  searchQuery,
  description,
  modelName,
  page,
}: SchemaComponentProps): ReturnType<typeof jsonLdScriptProps> | null => {
  if (
    shouldShowSchema({
      category,
      page,
    })
  ) {
    const offerCount = itemsCount > 50 ? itemsCount : undefined;
    const images = imageUrlsFromAdItems(items, 3);

    let { min: minimumPrice, max: maximumPrice } = getAdPriceRange(items);

    if (items?.[0]?.type.key === 'g') {
      minimumPrice = 0;
      maximumPrice = 0;
    }

    let brandAndModel: string | undefined;
    let brandSection = {};
    if (brandName !== undefined) {
      brandSection = {
        brand: {
          '@type': 'Brand',
          name: brandName,
        },
      };

      brandAndModel = brandName;
      if (modelName !== undefined) {
        brandAndModel = `${brandName} ${modelName}`;
      }
    }

    const categoryName = brandAndModel || searchQuery || category.label;

    const product: WithContext<Product> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: categoryName,
      image: images,
      description,
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: minimumPrice ? minimumPrice : 0,
        highPrice: maximumPrice ? maximumPrice : 0,
        priceCurrency: 'EUR',
        ...(offerCount ? { offerCount } : {}),
      },
      ...brandSection,
    };

    return jsonLdScriptProps<Product>(product);
  } else {
    return null;
  }
};

export default listingSchema;
